import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandableText from 'components/common/ExpandableText';
import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import React, { useState, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import DonorFeedbackFormModal from './relationManagement/DonorFeedbackFormModal';
import { format } from 'date-fns';
import { useAuthContext } from '../../context/authContext';
import {
  useDonor,
  useDonorFeedback,
  useCreateDonorFeedback,
  useUpdateDonorFeedback,
  useDeleteDonorFeedback
} from 'hooks/useDonors';
import LinkedReminders from 'components/reminders/LinkedReminders';

const DonorFeedback = () => {
  const {
    user: { id: userId },
    selectedBranchId
  } = useAuthContext();
  const { id } = useParams();
  const { donor } = useDonor(id);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [showAddFeedbackModel, setShowFeedbackModel] = useState(false);
  const [feedback, setFeedback] = useState({});

  const columns = useMemo(
    () => [
      // {
      //   accessor: 'ref',
      //   DonationHeader: 'Ref.',
      //   cellProps: {
      //     className: 'py-2'
      //   },
      //   Cell: rowData => (
      //     <Link to="#">{rowData.row.original.id.split('-')[0]}</Link>
      //   )
      // },
      {
        accessor: 'staff_name',
        Header: 'Staff Name',
        cellProps: { className: 'py-2' },
        Cell: rowData =>
          rowData.row.original.user
            ? `${rowData.row.original.user.first_name} ${rowData.row.original.user.last_name}`
            : '---'
      },
      {
        accessor: 'created_at',
        Header: 'Date',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => (
          <p
            className="text-truncate mb-0"
            style={{ maxWidth: '400px', fontSize: '1rem' }}
          >
            {format(
              new Date(rowData.row.original.created_at),
              'dd-MM-yyyy, hh:mm a'
            )}
          </p>
        )
      },
      {
        accessor: 'description',
        Header: 'Note',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => (
          <div className="d-flex align-items-start gap-2">
            {rowData.row.original.is_pinned && (
              <FontAwesomeIcon
                icon="thumbtack"
                className="text-warning mt-1"
                title="Pinned"
              />
            )}
            <ExpandableText text={rowData.row.original.description} />
          </div>
        )
      },
      {
        accessor: 'note_type',
        Header: 'Type',
        cellProps: { className: 'py-2' },
        Cell: rowData => rowData.row.original.note_type || '—'
      },
      {
        accessor: 'priority',
        Header: 'Priority',
        cellProps: {
          className: 'py-2'
        },
        Cell: rowData => (
          <p
            className={classNames('mb-0', {
              'text-danger': rowData.row.original.priority === 'Important'
            })}
            style={{ fontSize: '1rem' }}
          >
            {rowData.row.original.priority}
          </p>
        )
      },
      {
        accessor: 'id',
        Header: '',
        cellProps: {
          className: 'py-2 text-end'
        },
        disableSortBy: true,

        Cell: rowData => {
          const { id } = rowData.row;
          return (
            <TableRowAction
              onCLickEdit={() => {
                setShowFeedbackModel(true);
                setFeedback(rowData.row.original);
              }}
              onCLickDelete={() => {
                setDeleteId(id);
                setShowConfirmation(true);
              }}
              subject={permissionsKeyMap.donor.children.donors}
            />
          );
        }
      }
    ],
    []
  );

  // Fetch feedback
  const { feedback: donorFeedbackData, isLoading } = useDonorFeedback(id);

  // Memoize feedback data to prevent unnecessary re-renders

  // Mutations
  const { createFeedbackAsync, isCreating: addFeedbackModel } =
    useCreateDonorFeedback();
  const { updateFeedbackAsync } = useUpdateDonorFeedback();
  const { deleteFeedbackAsync } = useDeleteDonorFeedback();

  const handleAddNew = () => {
    setShowFeedbackModel(true);
  };

  const handleCreateDonorFeedbackComment = async values => {
    try {
      if (Object.keys(feedback).length !== 0) {
        await updateFeedbackAsync({
          id: values.id,
          data: {
            user_id: userId,
            description: values.description,
            priority: values.priority,
            note_type: values.note_type || null,
            is_pinned: Boolean(values.is_pinned)
          }
        });
      } else {
        await createFeedbackAsync({
          donar_id: id,
          user_id: userId,
          description: values.description,
          priority: values.priority,
          note_type: values.note_type || null,
          is_pinned: Boolean(values.is_pinned),
          branch_id: selectedBranchId
        });
      }
      setFeedback({});
      setShowFeedbackModel(false);
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteFeedbackAsync(deleteId);
      setShowConfirmation(false);
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  return (
    <>
      {showAddFeedbackModel && (
        <DonorFeedbackFormModal
          show={showAddFeedbackModel}
          onHide={() => setShowFeedbackModel(false)}
          onSubmit={handleCreateDonorFeedbackComment}
          loading={addFeedbackModel}
          isEdit={feedback ? true : false}
          initialValues={feedback}
        />
      )}
      {showConfirmation && (
        <ConfirmationModal
          show={showConfirmation}
          handleConfirm={handleConfirmDelete}
          onHide={() => setShowConfirmation(false)}
          title={'Confirmation'}
          body="You sure to delete this feedback?"
        />
      )}

      {/*Donor Feedback And Rating Section */}
      {/* <Card className="mb-3">
        <Card.DonationHeader className="p-4 pb-0">
          <Card.Title className="fs-2 fw-bold">Feedback & Ratings</Card.Title>
        </Card.DonationHeader>
        <Card.Body className="p-4">
          {weFeedBackData.map(item => (
            <Flex key={item.id} justifyContent={'between'}>
              <p className="text-800">{item.label}</p>
              <p className="text-800">
                <FontAwesomeIcon
                  icon={'circle'}
                  className={`me-2 ${item.iconColor}`}
                />
                {item.value}/{item.baseValue}
              </p>
            </Flex>
          ))}
        </Card.Body>
      </Card> */}

      <LinkedReminders
        entityType="donor"
        entityId={id}
        entityLabel={donor ? `${donor.first_name} ${donor.last_name}` : ''}
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={[...(donorFeedbackData || [])].sort(
              (a, b) =>
                Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned)) ||
                new Date(b.created_at) - new Date(a.created_at)
            )}
            columns={columns}
            title="Client Notes"
            showSearchInput
            searchInputPlaceHolder="Search"
            addNew
            onClickAddNew={handleAddNew}
            perPage={50}
            loading={isLoading}
            subject={permissionsKeyMap.donor.children.donors}
          />
        </Col>
      </Row>
    </>
  );
};

export default DonorFeedback;
